import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, GraduationCap, Star } from "@phosphor-icons/react";
import PilotCarIcon from "./PilotCarIcon";

/**
 * LiveActivityToast — premium bottom-right "social proof" memo.
 * Every few minutes (random 90-210s) a card slides in showing a recent activity:
 *  - proefles aangevraagd
 *  - pakket gekocht (Economy/Business/First Class)
 *  - tussentijdse toets gehaald
 *  - praktijkexamen gehaald (rijbewijs)
 * Worldwide names + Eindhoven/Helmond/regio cities for local credibility.
 * Slides in/out with a gold accent stroke that traces the card border.
 */

// Worldwide first names (Dutch, Turkish, Moroccan, Polish, Spanish, Indian,
// Eastern European, African, Asian) — reflects the diverse region.
const NAMES = [
  "Sara", "Emma", "Lars", "Yara", "Tijn", "Femke", "Daan", "Lotte", "Sem", "Eva",
  "Lieke", "Mees", "Bram", "Noa", "Luuk", "Fenna", "Stijn", "Mila", "Jens", "Sanne",
  "Mehmet", "Aylin", "Yusuf", "Ayşe", "Burak", "Zeynep", "Emre",
  "Mohammed", "Fatima", "Yassin", "Amira", "Karim", "Layla", "Omar", "Nour",
  "Kacper", "Wiktoria", "Mateusz", "Zofia", "Jakub",
  "Diego", "Lucia", "Mateo", "Sofia", "Andrés",
  "Arjun", "Priya", "Rohan", "Anika", "Aarav",
  "Chen", "Mei", "Hiroshi", "Yuki",
  "Kwame", "Ade", "Amara", "Tunde",
  "Liam", "Noah", "Olivia", "Charlotte", "Ethan",
  "Andrei", "Elena", "Dragos",
  "Robin", "Iris", "Pim", "Anouk", "Tess", "Thijs", "Floor",
];

const CITIES = [
  "Eindhoven", "Eindhoven", "Eindhoven",
  "Helmond", "Helmond", "Helmond",
  "Geldrop", "Nuenen", "Veldhoven", "Son", "Best",
  "Mierlo", "Aarle-Rixtel", "Asten", "Deurne",
  "Waalre", "Valkenswaard",
];

const PACKAGES = [
  { name: "Economy Class", color: "#5fa3ff" },
  { name: "Business Class", color: "#9bd47d" },
  { name: "First Class", color: "#a78bfa" },
];

const EVENTS = [
  {
    id: "proefles",
    icon: PilotCarIcon,
    iconWeight: "duotone",
    label: "Proefles aangevraagd",
    build: (name, city) => ({
      title: `${name} heeft een proefles aangevraagd`,
      sub: `${city} · zojuist binnen`,
    }),
    weight: 4,
  },
  {
    id: "pakket",
    icon: Star,
    iconWeight: "fill",
    label: "Pakket geboekt",
    build: (name, city, pkg) => ({
      title: `${name} koos voor ${pkg.name}`,
      sub: `${city} · pakket geboekt`,
      accent: pkg.color,
    }),
    weight: 3,
  },
  {
    id: "tussentijdse",
    icon: CheckCircle,
    iconWeight: "duotone",
    label: "TTT gehaald",
    build: (name, city) => ({
      title: `${name} slaagde voor de TTT`,
      sub: `${city} · ronde cirkel`,
    }),
    weight: 2,
  },
  {
    id: "rijbewijs",
    icon: GraduationCap,
    iconWeight: "fill",
    label: "Rijbewijs gehaald",
    build: (name, city) => ({
      title: `${name} haalde het rijbewijs in één keer`,
      sub: `${city} · welkom op de weg`,
    }),
    weight: 4,
  },
];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const pickWeighted = (arr) => {
  const total = arr.reduce((s, x) => s + (x.weight || 1), 0);
  let r = Math.random() * total;
  for (const item of arr) {
    r -= item.weight || 1;
    if (r <= 0) return item;
  }
  return arr[0];
};

const buildEvent = () => {
  const ev = pickWeighted(EVENTS);
  const name = pick(NAMES);
  const city = pick(CITIES);
  const pkg = pick(PACKAGES);
  const data = ev.build(name, city, pkg);
  const ago = Math.floor(Math.random() * 7) + 1; // 1-7 minutes
  return {
    key: `${ev.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    eventId: ev.id,
    Icon: ev.icon,
    iconWeight: ev.iconWeight,
    label: ev.label,
    initial: name.charAt(0).toUpperCase(),
    accent: data.accent || "#e0c068",
    title: data.title,
    sub: data.sub,
    ago,
  };
};

const LiveActivityToast = () => {
  const [visible, setVisible] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const timers = useRef([]);

  // Allow user to dismiss for the session
  useEffect(() => {
    if (typeof window === "undefined") return;
    const dismissed = sessionStorage.getItem("ap_live_dismissed");
    if (dismissed) setEnabled(false);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let mounted = true;

    const clearAll = () => {
      timers.current.forEach((t) => clearTimeout(t));
      timers.current = [];
    };

    const cycle = (firstDelay) => {
      // schedule next event between 90-210 seconds
      const showDelay = firstDelay;
      const t1 = setTimeout(() => {
        if (!mounted) return;
        setVisible(buildEvent());
        // hide after ~7s
        const t2 = setTimeout(() => {
          if (!mounted) return;
          setVisible(null);
          // queue next
          const next = 90000 + Math.random() * 120000; // 90s - 210s
          cycle(next);
        }, 7000);
        timers.current.push(t2);
      }, showDelay);
      timers.current.push(t1);
    };

    // First toast appears 12-25 seconds after page mount
    cycle(12000 + Math.random() * 13000);

    return () => {
      mounted = false;
      clearAll();
    };
  }, [enabled]);

  const dismiss = () => {
    setVisible(null);
    setEnabled(false);
    try { sessionStorage.setItem("ap_live_dismissed", "1"); } catch (e) { /* no-op */ }
  };

  // Layout: above the WhatsApp button (bottom-6 + ~56px = bottom-24)
  return (
    <div
      className="pointer-events-none fixed right-4 sm:right-6 bottom-24 sm:bottom-28 z-40 max-w-[92vw] sm:max-w-[360px] w-[340px]"
      data-testid="live-activity-container"
      aria-live="polite"
    >
      <AnimatePresence mode="wait">
        {visible && (
          <motion.div
            key={visible.key}
            initial={{ opacity: 0, x: 60, y: 6 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 80, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="pointer-events-auto"
            data-testid={`live-activity-toast-${visible.eventId}`}
          >
            <div className="live-toast relative overflow-hidden">
              {/* gold tracer along top edge */}
              <span className="live-toast-tracer" aria-hidden="true" />

              <div className="relative flex items-start gap-3 p-4 pr-8">
                {/* avatar */}
                <div
                  className="flex-shrink-0 w-10 h-10 rounded-full grid place-items-center text-sm font-semibold tracking-wide"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(224,192,104,0.25), rgba(224,192,104,0.05))",
                    border: "1px solid rgba(224,192,104,0.55)",
                    color: "#e0c068",
                    boxShadow: "0 0 16px rgba(224,192,104,0.18)",
                  }}
                  aria-hidden="true"
                >
                  {visible.initial}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="w-1.5 h-1.5 rounded-full pulse-dot"
                      style={{ background: visible.accent }}
                      aria-hidden="true"
                    />
                    <span
                      className="text-[10px] uppercase tracking-[0.22em] font-semibold"
                      style={{ color: visible.accent }}
                    >
                      {visible.label}
                    </span>
                  </div>

                  <div className="text-white text-sm leading-snug font-medium truncate-2">
                    {visible.title}
                  </div>
                  <div className="text-[11px] text-silver/80 mt-1 flex items-center gap-1.5">
                    <visible.Icon
                      size={12}
                      weight={visible.iconWeight}
                      style={{ color: visible.accent }}
                    />
                    <span className="truncate">
                      {visible.sub} · {visible.ago} min geleden
                    </span>
                  </div>
                </div>
              </div>

              {/* close button */}
              <button
                type="button"
                onClick={dismiss}
                className="absolute top-2 right-2 w-6 h-6 grid place-items-center rounded-full text-silver/60 hover:text-gold hover:bg-white/5 transition-colors"
                aria-label="Sluiten"
                data-testid="live-activity-dismiss"
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LiveActivityToast;
